import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { GoogleOAuthProvider } from '@react-oauth/google';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';
import ChatWidget from './components/ChatWidget';
// Pages
import LandingPage from './pages/LandingPage';
import MemberRegistration from './pages/MemberRegistration';
import MemberLogin from './pages/MemberLogin';
import MemberDashboard from './pages/MemberDashboard';
import MemberDashboardNew from './pages/MemberDashboardNew';
import MemberProfile from './pages/MemberProfile';
import EventCheckInPage from './pages/EventCheckInPage';
import NotificationPreferences from './pages/NotificationPreferences';
import AdminDashboard from './pages/AdminDashboard';
import MembersPage from './pages/MembersPage';
import AdminUsersPage from './pages/AdminUsersPage';
import EventsPage from './pages/EventsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import AnalyticsPage from './pages/AnalyticsPage';
import CertificatesPage from './pages/CertificatesPage';
import MemberEventsPage from './pages/MemberEventsPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import EmailVerification from './pages/EmailVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ResendVerification from './pages/ResendVerification';

// Auth Context
import { AuthProvider, useAuth } from './context/AuthContext';

// Protected Route Components
const MemberProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('token');
  const userType = localStorage.getItem('userType');

  if (!token || userType !== 'member') {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" />;
  }

  // Prevent members from accessing admin routes
  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

function App() {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

  return (
    <>
      <ChatWidget />
      <GoogleOAuthProvider clientId={googleClientId}>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<MemberLogin />} />
            <Route path="/member/login" element={<MemberLogin />} />
            <Route path="/register" element={<MemberRegistration />} />
            <Route path="/admin/login" element={<LandingPage />} />

            {/* Email Verification & Password Reset Routes */}
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/resend-verification" element={<ResendVerification />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Member Dashboard */}
            <Route path="/member/dashboard" element={
              <MemberProtectedRoute><MemberDashboardNew /></MemberProtectedRoute>
            } />
            <Route path="/member/profile" element={
              <MemberProtectedRoute><MemberProfile /></MemberProtectedRoute>
            } />
            {/* Public check-in route - uses Gmail authentication */}
            <Route path="/member/check-in" element={<EventCheckInPage />} />
            <Route path="/member/notifications" element={
              <MemberProtectedRoute><NotificationPreferences /></MemberProtectedRoute>
            } />
            <Route path="/member/events" element={
              <MemberProtectedRoute><MemberEventsPage /></MemberProtectedRoute>
            } />
            <Route path="/member/dashboard/old" element={
              <MemberProtectedRoute><MemberDashboard /></MemberProtectedRoute>
            } />

            {/* Admin Dashboard */}
            <Route path="/admin/dashboard" element={
              <AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>
            } />
            <Route path="/admin/members" element={
              <AdminProtectedRoute><MembersPage /></AdminProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <AdminProtectedRoute><AdminUsersPage /></AdminProtectedRoute>
            } />
            <Route path="/admin/events" element={
              <AdminProtectedRoute><EventsPage /></AdminProtectedRoute>
            } />
            <Route path="/admin/leaderboard" element={
              <AdminProtectedRoute><LeaderboardPage /></AdminProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <AdminProtectedRoute><AnalyticsPage /></AdminProtectedRoute>
            } />
            <Route path="/admin/certificates" element={
              <AdminProtectedRoute><CertificatesPage /></AdminProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <AdminProtectedRoute><AdminSettingsPage /></AdminProtectedRoute>
            } />
            </Routes>

            <ToastContainer
              position="top-right"
              autoClose={4000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
          </div>
        </Router>
      </AuthProvider>
    </GoogleOAuthProvider>
   </> 
  );
}

export default App;
